import api from 'config/api/protectedApi';
import { apiRoutes } from 'config/api/apiRoutes';
import useError from 'utils/useError';

import { ChargeResponse, IChargeData, IContactSalesFormData, ISubscriptionPlan, IPackageConsumption, ISubscriptionDetails, IPlanSummary } from 'models/subscriptionManagmentModel';
import { IAccountMarketplaceConnector } from 'models/connectorsModel';

export async function getSubscriptionDetails(languageCode: string): Promise<ISubscriptionDetails> {
  return api
    .get(apiRoutes.getSubscriptionDetails, {
      params: {
        languageCode: languageCode,
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

export async function getAccountPlanFeatureValue(code: string): Promise<string> {
  return api
    .get(apiRoutes.getAccountPlanFeatureValue, {
      params: {
        code: code,
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

export const useSubscriptionManagementApi = () => {
  const { throwException } = useError();

  async function createTapCharge(data: IChargeData): Promise<ChargeResponse> {
    return api
      .post(apiRoutes.createTapCharge, data)
      .then((response) => response.data)
      .catch((error) => {
        throwException(error);
        throw error;
      });
  }

  async function verifyCharge(data: { chargeId: string, planId: number, planName: string }): Promise<ChargeResponse> {
    return api
      .post(apiRoutes.verifyCharge, data)
      .then((response) => response.data)
      .catch((error) => {
        throwException(error);
        throw error;
      });
  }

  async function getPlanSummary(planId: number, languageCode: string | undefined): Promise<IPlanSummary> {
    return api
      .get(apiRoutes.getPlanSummary, {
        params: {
          planId: planId,
          languageCode: languageCode
        },
      })
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  async function getAccountMarketplaceConnectors(): Promise<IAccountMarketplaceConnector[]> {
    return api
      .get(apiRoutes.getAccountMarketplaceConnectors)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  async function listPackageConsumption(): Promise<IPackageConsumption[]> {
    return api
      .get(apiRoutes.getPackageConsumptionList)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  async function getSubscriptionPlans(languageCode: string): Promise<ISubscriptionPlan[]> {
    return api
      .get(apiRoutes.getSubscriptionPlans, {
        params: {
          languageCode: languageCode,
        },
      })
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  async function requestCustomPlan(data: IContactSalesFormData): Promise<boolean> {
    return api
      .post(apiRoutes.requestCustomPlan, data)
      .then(() => true)
      .catch((error) => {
        throwException(error);
        throw error;
      });
  }

  async function downgradePlan(planId: number, planName: string, planEffectiveDate: string): Promise<boolean> {
    return api
      .post(apiRoutes.downgradeSubscriptionPlan, { planId, planName, planEffectiveDate })
      .then(() => true)
      .catch((error) => {
        throwException(error);
        throw error;
      });
  }

  async function cancelPlan(planId: number, planExpiryDate: string): Promise<boolean> {
    return api
      .post(apiRoutes.cancelSubscriptionPlan, { planId, planExpiryDate })
      .then(() => true)
      .catch((error) => {
        throwException(error);
        throw error;
      });
  }

  return {
    createTapCharge,
    verifyCharge,
    getSubscriptionPlans,
    requestCustomPlan,
    getAccountMarketplaceConnectors,
    listPackageConsumption,
    downgradePlan,
    cancelPlan,
    getPlanSummary
  };
};
